// Aignoz Phase 5 — auth context + hooks + axios interceptor.
// Bearer JWT stored in localStorage; hydrates user on mount via GET /auth/me.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const LS_TOKEN = "aignoz.auth.token";
const LS_USER = "aignoz.auth.user";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Format any FastAPI error payload → readable string.
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function safeRead(key) {
  try {
    const v = localStorage.getItem(key);
    return v ?? null;
  } catch { return null; }
}
function safeWrite(key, value) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value));
  } catch { /* ignore */ }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => safeRead(LS_TOKEN));
  const [user, setUser] = useState(() => {
    const raw = safeRead(LS_USER);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  const [isLoading, setLoading] = useState(!!token);
  const [error, setError] = useState(null);
  const interceptorRef = useRef({ req: null, res: null });

  // Persist token/user to localStorage
  useEffect(() => { safeWrite(LS_TOKEN, token); }, [token]);
  useEffect(() => { safeWrite(LS_USER, user); }, [user]);

  // Axios interceptor — attach Bearer token + auto-logout on 401
  useEffect(() => {
    // Clean up previous
    if (interceptorRef.current.req != null) {
      axios.interceptors.request.eject(interceptorRef.current.req);
    }
    if (interceptorRef.current.res != null) {
      axios.interceptors.response.eject(interceptorRef.current.res);
    }
    const reqId = axios.interceptors.request.use((config) => {
      const t = safeRead(LS_TOKEN);
      if (t) config.headers = { ...config.headers, Authorization: `Bearer ${t}` };
      return config;
    });
    const resId = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const isAuthCall = /\/api\/auth\//.test(err?.config?.url || "");
        if (err?.response?.status === 401 && !isAuthCall) {
          // Auto-clear stale creds on 401 from any protected endpoint.
          safeWrite(LS_TOKEN, null);
          safeWrite(LS_USER, null);
          setToken(null);
          setUser(null);
        }
        return Promise.reject(err);
      },
    );
    interceptorRef.current = { req: reqId, res: resId };
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []);

  // Hydrate user via /auth/me on mount when a token exists
  useEffect(() => {
    let dead = false;
    async function hydrate() {
      if (!token) { setLoading(false); return; }
      try {
        const { data } = await axios.get(`${API_BASE}/api/auth/me`);
        if (!dead) setUser(data);
      } catch {
        if (!dead) { setToken(null); setUser(null); }
      } finally {
        if (!dead) setLoading(false);
      }
    }
    hydrate();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async ({ email, password, persona }) => {
    setError(null);
    try {
      const body = { email, password };
      if (persona) body.persona = persona;
      const { data } = await axios.post(`${API_BASE}/api/auth/login`, body);
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      return { ok: false, error: msg };
    }
  }, []);

  const register = useCallback(async ({ email, password, persona, name }) => {
    setError(null);
    try {
      const { data } = await axios.post(`${API_BASE}/api/auth/register`, {
        email, password, persona, name,
      });
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      return { ok: false, error: msg };
    }
  }, []);

  const demoLogin = useCallback(async () => {
    setError(null);
    try {
      const { data } = await axios.post(`${API_BASE}/api/auth/demo`);
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      return { ok: false, error: msg };
    }
  }, []);

  const updatePersona = useCallback(async (persona) => {
    if (!persona) return { ok: false };
    try {
      const { data } = await axios.patch(`${API_BASE}/api/auth/me`, { persona });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { ok: false, error: msg };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API_BASE}/api/auth/logout`); } catch { /* ignore */ }
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    token,
    persona: user?.persona || null,
    isAuthenticated: !!user && !!token,
    isLoading,
    error,
    login, register, demoLogin, updatePersona, logout,
  }), [user, token, isLoading, error, login, register, demoLogin, updatePersona, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
